import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { buildSagelMeetingUrl, resolveMeetingUrl, SupportedMeetingLanguage } from '../meetings/meeting-link.util';

const API_BASE = `${environment.baseUrl}/api/v1/general-appointments`;

@Component({
    selector: 'app-reschedule-appointment',
    templateUrl: './reschedule-appointment.component.html',
    styleUrls: ['./reschedule-appointment.component.scss'],
    standalone: false
})
export class RescheduleAppointmentComponent implements OnInit {
  companyName = '';
  appointmentId = '';

  // Form fields
  name = '';
  startDate: Date | string = '';
  startTime = '';
  email = '';
  phone = '';
  notes = '';
  meetingUrl = '';

  // State
  isLoading = false;
  isSaving = false;
  success: boolean | null = null;
  errorText = '';

  private userId = '';
  private readonly timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private lang: SupportedMeetingLanguage = 'en';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
  ) {
    const supported = ['en', 'tr', 'es', 'el', 'de'];
    const browserLang = navigator.language.split('-')[0];
    this.lang = (supported.includes(browserLang) ? browserLang : 'en') as SupportedMeetingLanguage;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const company = params.get('companyName');
      const id = params.get('reservationId');
      if (company && id) {
        this.companyName = decodeURIComponent(company);
        this.appointmentId = id;
        this.fetchAppointment();
      } else {
        this.success = false;
        this.errorText = 'Invalid reschedule link.';
      }
    });
  }

  private fetchAppointment(): void {
    this.isLoading = true;
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: res => {
          this.userId = res.user_id;
          this.http
            .get<any>(`${API_BASE}/public/${this.appointmentId}`)
            .subscribe({
              next: data => {
                const a = data?.data ?? data;
                if (a && a.id) {
                  // Public endpoint returns anonymized data — only scheduling fields
                  this.startDate = String(a.appointment_date || '').slice(0, 10);
                  this.startTime = String(a.appointment_time || '').slice(0, 5);
                } else {
                  this.success = false;
                  this.errorText = 'Appointment not found.';
                }
                this.isLoading = false;
              },
              error: () => {
                this.isLoading = false;
                this.success = false;
                this.errorText = 'Could not load appointment details.';
              },
            });
        },
        error: () => {
          this.isLoading = false;
          this.success = false;
          this.errorText = 'Could not find this company.';
        },
      });
  }

  get timeOptions(): string[] {
    const times: string[] = [];
    for (let h = 6; h <= 22; h++) {
      for (const m of [0, 15, 30, 45]) {
        times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return times;
  }

  save(): void {
    if (this.isSaving) return;
    if (!this.name.trim() || !this.email.trim()) {
      this.errorText = 'Please fill in your name and email.';
      return;
    }
    this.isSaving = true;
    this.errorText = '';

    const dateKey = this.getDateKey(this.startDate) || '';
    const updatedMeetingUrl = resolveMeetingUrl(
      this.meetingUrl,
      buildSagelMeetingUrl({
        companyName: this.companyName,
        appointmentDate: dateKey,
        appointmentTime: this.startTime,
        timezone: this.timezone,
        language: this.lang,
      }),
    );

    const payload = {
      customer_name: this.name,
      customer_email: this.email,
      customer_phone: this.phone || null,
      appointment_date: dateKey,
      appointment_time: this.startTime.length === 5 ? `${this.startTime}:00` : this.startTime,
      notes: this.notes || null,
      meeting_url: updatedMeetingUrl,
    };

    this.http.post<any>(`${API_BASE}/public/${this.appointmentId}`, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.success = true;
        this.sendUpdateEmail(dateKey, payload.appointment_time, updatedMeetingUrl);
      },
      error: err => {
        this.isSaving = false;
        this.success = false;
        this.errorText = `Could not update your appointment (${err?.status ?? 'error'}). Please try again.`;
      },
    });
  }

  private sendUpdateEmail(dateStr: string, time: string, meetingUrl: string): void {
    if (!this.email) return;

    const enc = encodeURIComponent(this.companyName);
    const editLink = `${window.location.origin}/#/reschedule-general/${enc}/${this.appointmentId}`;
    const dateObj = new Date(`${dateStr}T00:00:00`);
    const longDate = dateObj.toLocaleDateString('en-GB', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const displayTime = time.slice(0, 5);

    const phoneRow = this.phone
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Phone</td><td style="padding:6px 0;color:#1f2a44;">${this.phone}</td></tr>`
      : '';
    const notesRow = this.notes
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Notes</td><td style="padding:6px 0;color:#1f2a44;">${this.notes}</td></tr>`
      : '';
    const meetingRow = meetingUrl
      ? `<tr><td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Meeting</td><td style="padding:6px 0;color:#1f2a44;"><a href="${meetingUrl}" style="color:#1a3d8f;word-break:break-all;">${meetingUrl}</a></td></tr>`
      : '';

    const html = `
<div style="font-family:Arial,sans-serif;background:#f5f5f5;padding:24px 16px;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #e0e0e0;border-radius:4px;">
    <tr>
      <td style="padding:24px 32px;border-bottom:2px solid #1a3d8f;">
        <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">${this.companyName}</div>
        <div style="font-size:20px;font-weight:600;color:#1a1a1a;">Appointment Updated</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <div style="font-size:13px;color:#888;margin-bottom:4px;">${longDate}</div>
        <div style="font-size:22px;font-weight:600;color:#1a1a1a;">${displayTime}</div>
        <div style="font-size:12px;color:#888;margin-top:4px;">${this.timezone}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <table width="100%" cellspacing="0" cellpadding="0" style="font-size:13px;color:#333;line-height:1.6;">
          <tr>
            <td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Name</td>
            <td style="padding:6px 0;color:#1f2a44;">${this.name}</td>
          </tr>
          <tr>
            <td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Email</td>
            <td style="padding:6px 0;color:#1f2a44;">${this.email}</td>
          </tr>
          ${phoneRow}
          ${notesRow}
          ${meetingRow}
          <tr>
            <td style="width:110px;color:#6c7794;padding:6px 0;vertical-align:top;">Reschedule</td>
            <td style="padding:6px 0;color:#1f2a44;"><a href="${editLink}" style="color:#1a3d8f;">Update again</a></td>
          </tr>
        </table>
      </td>
    </tr>
    ${meetingUrl ? `
    <tr>
      <td style="padding:20px 32px;text-align:center;">
        <a href="${meetingUrl}" style="display:inline-block;padding:10px 22px;background:#1a3d8f;color:#fff;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;">Join Meeting</a>
      </td>
    </tr>` : ''}
  </table>
</div>`;

    this.http.post(`${environment.baseUrl}/email_notifications/info`, {
      header: `Appointment Updated - ${this.companyName}`,
      content: html,
      to_addr: this.email,
    }).subscribe();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private getDateKey(value: Date | string | null | undefined): string | null {
    if (!value) return null;
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split(/[./]/);
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) return null;
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }
  get formattedDate(): string {
    const key = this.getDateKey(this.startDate);
    if (!key) return '';
    return new Date(key + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    });
  }
}
